import Image, { type ImageProps } from 'next/image';
import React from 'react';

/**
 * Payload Media doc shape (subset used by the renderer).
 */
export interface PayloadMedia {
  id?: number;
  url?: string;
  alt?: string;
  width?: number | null;
  height?: number | null;
  mimeType?: string;
  focalX?: number | null; // 0..100 (Payload stores as percent)
  focalY?: number | null;
  sizes?: Record<
    string,
    {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
    } | null
  > | null;
}

/**
 * Build a srcset string from Payload's generated image sizes.
 * Each variant added in width-ascending order.
 */
function buildSrcSet(media: PayloadMedia): string | undefined {
  if (!media.sizes) return undefined;
  const entries = Object.values(media.sizes)
    .filter((s): s is { url: string; width: number } => Boolean(s?.url && s?.width))
    .sort((a, b) => a.width - b.width)
    .map((s) => `${s.url} ${s.width}w`);
  if (media.url && media.width) entries.push(`${media.url} ${media.width}w`);
  return entries.length ? entries.join(', ') : undefined;
}

/**
 * Pick the best size variant for a target width.
 * Returns the smallest variant whose width is >= targetWidth, or the original.
 */
export function pickSize(media: PayloadMedia, targetWidth: number): {
  url: string;
  width: number;
  height: number;
} | null {
  if (!media.url) return null;
  const candidates: { url: string; width: number; height: number }[] = [];
  for (const s of Object.values(media.sizes || {})) {
    if (s?.url && s.width && s.height) {
      candidates.push({ url: s.url, width: s.width, height: s.height });
    }
  }
  if (media.url && media.width && media.height) {
    candidates.push({ url: media.url, width: media.width, height: media.height });
  }
  candidates.sort((a, b) => a.width - b.width);
  return candidates.find((c) => c.width >= targetWidth) || candidates[candidates.length - 1] || null;
}

export interface PayloadImageProps extends Omit<ImageProps, 'src' | 'alt' | 'width' | 'height'> {
  media: PayloadMedia | null | undefined;
  /** Override alt (defaults to media.alt). */
  alt?: string;
  /** Target rendered width in px (used to pick the best variant). Defaults to media.width. */
  targetWidth?: number;
  /** Optional className passthrough. */
  className?: string;
  /** If true, do not honour focal point. */
  ignoreFocalPoint?: boolean;
  /** Sizes attribute hint (Next.js). */
  sizes?: string;
}

/**
 * `<PayloadImage media={...} />`
 *
 * Drop-in wrapper around `next/image` that:
 *  - reads intrinsic width/height from the media doc (no CLS)
 *  - emits a srcSet covering all generated Payload sizes
 *  - applies focal point as `objectPosition`
 *  - falls back gracefully if the media doc is missing
 */
export const PayloadImage: React.FC<PayloadImageProps> = ({
  media,
  alt,
  targetWidth,
  className,
  ignoreFocalPoint,
  sizes,
  style,
  ...rest
}) => {
  if (!media?.url) return null;

  const variant =
    targetWidth != null
      ? pickSize(media, targetWidth)
      : { url: media.url, width: media.width || 1200, height: media.height || 800 };

  if (!variant) return null;

  const srcSet = buildSrcSet(media);

  // Focal point — Payload stores as percent (0..100)
  const fx = media.focalX;
  const fy = media.focalY;
  const objectPosition =
    !ignoreFocalPoint && (fx != null || fy != null)
      ? `${fx ?? 50}% ${fy ?? 50}%`
      : undefined;

  return (
    <Image
      src={variant.url}
      alt={alt ?? media.alt ?? ''}
      width={variant.width}
      height={variant.height}
      sizes={sizes ?? '(min-width: 1024px) 50vw, 100vw'}
      className={className}
      style={{ ...(objectPosition ? { objectPosition } : {}), ...style }}
      {...(srcSet ? ({ srcSet } as any) : {})}
      {...rest}
    />
  );
};

export default PayloadImage;
